import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { validateDateRange } from '../utils/dateHelpers';
import { monitorQuery, monitorParallelQueries } from '../utils/queryMonitoring';
// Chart.js imports moved to CompactChart component
import { useDebounce } from '../hooks/useDebounce';
import DateRangeSelector from '../components/common/DateRangeSelector';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import CompactChart from '../components/common/CompactChart';
import ChartModal from '../components/common/ChartModal';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, hasPermission, canViewAllData, canViewAllReports, getRbacMatchNames } from '../utils/permissions';
import { useUserRole } from '../hooks/useUserRole';
import ExportButton from '../components/common/ExportButton';
import { useUrlState } from '../hooks/useUrlState';
import { exportData } from '../utils/exportUtils';

// Chart.js registration handled by CompactChart component

const SalesRepReportsPage = ({ user }) => {
    const { can } = usePermissions(user);
    const userRole = useUserRole(user);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [orders, setOrders] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [users, setUsers] = useState([]);
    const [range, setRange] = useUrlState('range', '30');
    const [customStart, setCustomStart] = useUrlState('customStart', '');
    const [customEnd, setCustomEnd] = useUrlState('customEnd', '');
    const [selectedReps, setSelectedReps] = useUrlState('selectedReps', []);
    // Pending filter state — all filters wait for Apply button
    const [pendingRange, setPendingRange] = useState(range || '30');
    const [pendingCustomStart, setPendingCustomStart] = useState(customStart || '');
    const [pendingCustomEnd, setPendingCustomEnd] = useState(customEnd || '');
    const [pendingReps, setPendingReps] = useState([]);
    const [showRepDropdown, setShowRepDropdown] = useState(false);
    const [cachedData, setCachedData] = useState(null);
    const [usingCache, setUsingCache] = useState(false);
    const [customRangeApplied, setCustomRangeApplied] = useUrlState('customRangeApplied', false);

    const [salesChartConfig, setSalesChartConfig] = useState(null);
    const [repChartConfig, setRepChartConfig] = useState(null);


    const repDropdownRef = useRef(null);
    const fetchingRef = useRef(false); // Prevent concurrent fetches

    // Fetch data
    useEffect(() => {
        // Wait for user and userRole to load
        if (!user || !user.uid) return;
        if (userRole === undefined || userRole === null) return;

        const fetchData = async () => {
            // Intelligent fetch collision prevention - only prevent if same parameters
            const currentFetchKey = `${range}_${customStart}_${customEnd}_${customRangeApplied}_${selectedReps.join(',')}`;
            if (fetchingRef.current === currentFetchKey) {
                console.log('[SalesRepReports] Same fetch already in progress, skipping');
                return;
            }
            
            try {
                fetchingRef.current = currentFetchKey;
                setLoading(true);
                
                // Skip fetching if custom range is selected but not applied yet
                if (range === 'custom' && !customRangeApplied) {
                    setLoading(false);
                    fetchingRef.current = false;
                    return;
                }
                
                // Validate date range before fetching
                const validation = validateDateRange(range, customStart, customEnd);
                if (!validation.isValid) {
                    setToast({ message: validation.error, type: 'error' });
                    setLoading(false);
                    return;
                }
                
                // Check permissions FIRST (needed for cache filtering)
                // Data visibility: adminViewOnly sees all data regardless of custom permissions
                const canViewAll = canViewAllReports(user) || can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL);
                const userDisplayName = user?.displayName;
                
                // Determine if we can use cached data
                const isStandardRange = ['7', '14', '30', '90', '180', '365', 'ytd'].includes(range);
                const hasRepFilter = selectedReps.length > 0;
                
                // Try to use cached data for standard ranges without rep filter.
                // Non-admin users bypass the cache because it was generated with the old
                // (N * 24h) date-range logic; the client now uses ((N - 1) * 24h). Bypassing
                // ensures non-admin numbers match the Overview / Order Reports live queries.
                if (isStandardRange && !hasRepFilter && canViewAll) {
                    const cacheId = range === 'ytd' 
                        ? 'summary_ytd' 
                        : `summary_${range.padStart(2, '0')}d`;
                    
                    try {
                        const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), cacheId));
                        
                        if (cacheDoc.exists()) {
                            const data = cacheDoc.data();
                            const now = new Date();
                            const lastUpdated = data.lastUpdated?.toDate?.() || new Date(0);
                            const ageMinutes = Math.floor((now - lastUpdated) / (1000 * 60));
                            
                            // Use cache if less than 12 hours old (generation runs every 8 hours)
                            if (ageMinutes < 720) {
                                console.log(`[SalesRepReports] ✅ Using cached data (${ageMinutes} min old, cache ID: ${cacheId})`);
                                // RBAC: Filter cached data for non-admin users
                                let filteredData = { ...data };
                                
                                if (!canViewAll && user?.displayName) {
                                    const matchNames = getRbacMatchNames(user);
                                    const matchSet = new Set(matchNames);
                                    console.log('[SalesRepReports] Filtering cache for non-admin:', matchNames);
                                    
                                    // Filter repPerformance to only show current user (multi-name matching)
                                    if (data.repPerformance) {
                                        filteredData.repPerformance = data.repPerformance.filter(
                                            rep => matchSet.has(rep.name?.toLowerCase().trim())
                                        );
                                    }
                                    
                                    // Filter timeSeriesByRep to only show current user (find matching keys)
                                    if (data.timeSeriesByRep) {
                                        const matchingTimeSeries = {};
                                        for (const [repName, series] of Object.entries(data.timeSeriesByRep)) {
                                            if (matchSet.has(repName.toLowerCase().trim())) {
                                                matchingTimeSeries[repName] = series;
                                            }
                                        }
                                        filteredData.timeSeriesByRep = matchingTimeSeries;
                                    }
                                    
                                    // Update summary to reflect only user's data
                                    const userPerf = filteredData.repPerformance?.[0];
                                    if (userPerf) {
                                        filteredData.summary = {
                                            totalRevenue: userPerf.revenue || 0,
                                            orderCount: userPerf.orders || 0,
                                            avgOrderValue: userPerf.orders > 0 ? userPerf.revenue / userPerf.orders : 0,
                                            winRate: userPerf.winRate || 0
                                        };
                                    } else {
                                        filteredData.summary = {
                                            totalRevenue: 0,
                                            orderCount: 0,
                                            avgOrderValue: 0,
                                            winRate: 0
                                        };
                                    }
                                }
                                
                                setCachedData(filteredData);
                                setUsingCache(true);
                                
                                // Still need to fetch users for the dropdown
                                // BEST PRACTICE: Add limit to prevent hotspotting on high read rates
                                const usersSnap = await monitorQuery('fetch-users-salesrep-reports', () =>
                                    getDocs(query(
                                        collection(db, collectionPath('users')),
                                        limit(1000)
                                    ))
                                );
                                setUsers(usersSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
                                
                                setLoading(false);
                                return;
                            }
                        }
                    } catch (cacheErr) {
                        console.warn('[SalesRepReports] Cache fetch failed:', cacheErr);
                    }
                }
                
                // OPTIMIZED: Smart query strategy based on date range
                if (process.env.NODE_ENV === 'development' && false) {
                    console.log('[SalesRepReports] Starting optimized data fetch...');
                }
                
                let ordersQuery;
                if (range === 'custom' && customRangeApplied) {
                    // For custom ranges, use date_internal (YYYY-MM-DD) for consistency with all other reports
                    console.log('[SalesRepReports] Using date_internal filtered query for custom range:', {
                        start: start.toISOString(),
                        end: end.toISOString()
                    });
                    
                    // Convert to YYYY-MM-DD format for date_internal field comparison
                    const startDateStr = start.toISOString().split('T')[0]; // "2026-03-01"
                    const endDateStr = end.toISOString().split('T')[0];     // "2026-03-24"
                    
                    console.log('[SalesRepReports] Using date_internal range:', {
                        startDateStr,
                        endDateStr,
                        inputDateRange: `${customStart} to ${customEnd}`
                    });
                    
                    // Use date_internal (YYYY-MM-DD) for indexed range query
                    // Matches OrderReportsPage API, ComparisonReportsPage, and OrdersPage
                    ordersQuery = query(
                        collection(db, collectionPath('orders')),
                        where('date_internal', '>=', startDateStr),
                        where('date_internal', '<=', endDateStr),
                        orderBy('date_internal', 'desc'),
                        limit(2000) // Reasonable limit for custom date ranges
                    );
                    
                    console.log('[SalesRepReports] Executing date-filtered query...');
                } else {
                    // Standard ranges (7/14/30/90/180/365/ytd) when cache is bypassed —
                    // for example non-admin users whose totals must match the live Overview.
                    // Use the same date_internal indexed range query as OrderReportsPage so
                    // we don't fall back to an unindexed 5000-doc scan that returns arbitrary
                    // pre-2026 orders and drops everything during client-side date filtering.
                    const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                    const startDateStr = toDateStr(start);
                    const endDateStr = toDateStr(end);
                    console.log('[SalesRepReports] Using date_internal filtered query for standard range:', {
                        range,
                        startDateStr,
                        endDateStr
                    });
                    ordersQuery = query(
                        collection(db, collectionPath('orders')),
                        where('date_internal', '>=', startDateStr),
                        where('date_internal', '<=', endDateStr),
                        orderBy('date_internal', 'desc'),
                        limit(5000)
                    );
                }
                
                // Use parallel queries with monitoring for performance tracking
                const [ordersSnap, oppsSnap, usersSnap] = await monitorParallelQueries([
                    { name: 'fetch-orders-salesrep-reports', fn: () => getDocs(ordersQuery) },
                    { name: 'fetch-opportunities-salesrep-reports', fn: () => getDocs(query(collection(db, collectionPath('opportunities')), limit(2000))) },
                    { name: 'fetch-users-salesrep-reports-nonecache', fn: () => getDocs(query(collection(db, collectionPath('users')), limit(1000))) }
                ]);

                console.log('[SalesRepReports] Fetched data:', {
                    ordersCount: ordersSnap.docs.length,
                    oppsCount: oppsSnap.docs.length,
                    usersCount: usersSnap.docs.length
                });
                
                // DEBUG: If custom query returned 0 orders, check if there are any recent orders at all
                if (range === 'custom' && customRangeApplied && ordersSnap.docs.length === 0) {
                    console.log('[SalesRepReports] Custom query returned 0 orders. Checking for recent orders...');
                    
                    // Check for any orders from the last 30 days using date_internal
                    const thirtyDaysAgo = new Date();
                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                    const thirtyDaysStr = thirtyDaysAgo.toISOString().split('T')[0];
                    
                    const recentCheckQuery = query(
                        collection(db, collectionPath('orders')),
                        where('date_internal', '>=', thirtyDaysStr),
                        orderBy('date_internal', 'desc'),
                        limit(10)
                    );
                    
                    try {
                        const recentSnap = await getDocs(recentCheckQuery);
                        console.log('[SalesRepReports] Recent orders check:', {
                            ordersFound: recentSnap.docs.length,
                            searchPeriod: 'last 30 days',
                            sampleDates: recentSnap.docs.slice(0, 3).map(doc => {
                                const data = doc.data();
                                return {
                                    id: doc.id,
                                    date_internal: data.date_internal
                                };
                            })
                        });
                    } catch (error) {
                        console.warn('[SalesRepReports] Recent orders check failed:', error);
                    }
                }

                // Map orders with proper date parsing (MATCH ReportsPage.jsx)
                let ordersData = ordersSnap.docs.map(d => {
                    const data = d.data();
                    
                    // Use date_internal as primary date field for consistency across all reports
                    // Priority: date_internal -> date -> creation_time
                    let orderDate = null;
                    if (data.date_internal) {
                        orderDate = data.date_internal;
                    } else if (data.date) {
                        orderDate = data.date;
                    } else if (data.creation_time) {
                        orderDate = data.creation_time;
                    }
                    
                    // Handle both Firestore Timestamp objects and date strings
                    let dateObj = null;
                    if (orderDate) {
                        if (typeof orderDate.toDate === 'function') {
                            // Firestore Timestamp
                            dateObj = orderDate.toDate();
                        } else {
                            // String date
                            dateObj = new Date(orderDate);
                            // Validate parsed date
                            if (isNaN(dateObj.getTime())) {
                                dateObj = null;
                            }
                        }
                    }
                    
                    return {
                        id: d.id,
                        ...data,
                        total: parseFloat(data.amount) || 0,
                        date: orderDate || data.date || data.date_internal,
                        dateObj: dateObj
                    };
                });

                // DEBUG: Basic order date analysis (simplified)
                if (ordersData.length > 0) {
                    const sampleOrder = ordersData[0];
                    const dateFieldUsed = range === 'custom' && customRangeApplied ? 'creation_time (forced)' : 
                        sampleOrder.date_internal ? 'date_internal' : 
                        sampleOrder.date ? 'date' : 'creation_time';
                    
                    console.log('[SalesRepReports] Data fetch completed:', {
                        ordersCount: ordersData.length,
                        sampleOrderDate: sampleOrder.dateObj ? sampleOrder.dateObj.toISOString() : 'no date',
                        queryType: range === 'custom' && customRangeApplied ? 'server-filtered' : 'full-fetch',
                        dateFieldUsed: dateFieldUsed,
                        customRangeMode: range === 'custom' && customRangeApplied
                    });
                }

                // Filter out invalid dates
                ordersData = ordersData.filter(order => {
                    if (!order.dateObj || isNaN(order.dateObj.getTime())) {
                        return false;
                    }
                    return true;
                });

                // Filter orders for non-admin users by commissions (multi-name matching)
                // canViewAll and userDisplayName already declared at top of function
                
                if (!canViewAll && userDisplayName) {
                    const matchNames = getRbacMatchNames(user);
                    const matchSet = new Set(matchNames);
                    ordersData = ordersData.filter(order => {
                        if (!Array.isArray(order.commissions_displayNames)) return false;
                        return order.commissions_displayNames.some(name => 
                            name && matchSet.has(name.toLowerCase().trim())
                        );
                    });
                }
                
                console.log('[SalesRepReports] After processing:', {
                    originalOrdersCount: ordersSnap.docs.length,
                    validDateOrdersCount: ordersData.length,
                    canViewAll,
                    matchNames: getRbacMatchNames(user),
                    finalOrdersCount: ordersData.length
                });
                
                setOrders(ordersData);
                
                // Filter opportunities for non-admin users by uid (assigned user)
                let oppsData = oppsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                if (!canViewAll && user?.uid) {
                    oppsData = oppsData.filter(opp => opp.uid === user.uid);
                }
                setOpportunities(oppsData);
                
                setUsers(usersSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
                
                setLoading(false);
                fetchingRef.current = false;
            } catch (err) {
                console.error('[SalesRepReports] Error fetching data:', err);
                setToast({ message: `Error loading data: ${err.message}`, type: 'error' });
                setLoading(false);
                fetchingRef.current = false;
            }
        };

        fetchData();
    }, [user?.uid, userRole, range, selectedReps, customStart, customEnd, customRangeApplied, can]);

    // Manual cache refresh function (admin only)
    const handleManualRefresh = async () => {
        if (!can(FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH)) {
            alert('Only admins can manually refresh the cache');
            return;
        }

        // Check cache age - prevent refresh if data is too recent (< 10 minutes)
        if (cachedData && cachedData.lastUpdated) {
            const cacheAge = Date.now() - cachedData.lastUpdated.toDate().getTime();
            const minRefreshInterval = 5 * 60 * 1000; // 5 minutes in milliseconds
            
            if (cacheAge < minRefreshInterval) {
                const remainingTime = Math.ceil((minRefreshInterval - cacheAge) / 60000);
                alert(`Cache was refreshed recently. Please wait ${remainingTime} more minute(s) before refreshing again.`);
                return;
            }
        }

        const confirmed = window.confirm(
            'This will refresh the reports cache for all date ranges. ' +
            'It may take 30-60 seconds. Continue?'
        );
        
        if (!confirmed) return;

        try {
            setLoading(true);
            const refreshCache = httpsCallable(functions, 'refreshReportsCache');
            const result = await refreshCache();
            alert(result.data.message || 'Cache refreshed successfully!');
            
            // Reload the page data
            window.location.reload();
        } catch (error) {
            console.error('[SalesRepReports] Manual refresh failed:', error);
            alert(`Failed to refresh cache: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle applying custom date range
    const handleApplyCustomRange = () => {
        if (!customStart || !customEnd) {
            setToast({ message: 'Please select both start and end dates', type: 'error' });
            return;
        }
        
        const startDate = new Date(customStart);
        const endDate = new Date(customEnd);
        
        if (startDate > endDate) {
            setToast({ message: 'Start date must be before end date', type: 'error' });
            return;
        }
        
        // Force use of live data for custom ranges
        setUsingCache(false);
        setCachedData(null);
        setCustomRangeApplied(true);
    };

    // Reset custom range when switching to non-custom ranges
    useEffect(() => {
        if (range !== 'custom') {
            setCustomRangeApplied(false);
        }
    }, [range]);

    // When DateRangeSelector Apply sets both custom dates, mark as applied and force live fetch
    // (Only needed when URL state changes externally, e.g., browser back/forward)
    useEffect(() => {
        if (range === 'custom' && customStart && customEnd) {
            setUsingCache(false);
            setCachedData(null);
            setCustomRangeApplied(true);
        }
    }, [customStart, customEnd, range, setCustomRangeApplied]);

    // Calculate date range
    // Convention (matches src/utils/dateHelpers.js): "Last N days" = today + (N-1) prior
    // days = N calendar days ending today. Start is normalised to local midnight and end
    // to end-of-day so date_internal (YYYY-MM-DD) comparisons behave consistently.
    const getDateRange = (range, customStart, customEnd) => {
        const now = new Date();
        let start;
        let end = now;
        if (range === 'custom') {
            // Return fallback dates if custom dates are not set
            if (!customStart || !customEnd) {
                return {
                    start: now,
                    end: now
                };
            }
            // Parse custom dates as local dates to avoid timezone issues
            const startParts = customStart.split('-');
            const endParts = customEnd.split('-');
            // Validate that we have valid date parts
            if (startParts.length !== 3 || endParts.length !== 3) {
                return {
                    start: now,
                    end: now
                };
            }
            start = new Date(startParts[0], startParts[1] - 1, startParts[2]);
            end = new Date(endParts[0], endParts[1] - 1, endParts[2], 23, 59, 59, 999);
        } else if (range === 'ytd') {
            start = new Date(now.getFullYear(), 0, 1);
        } else if (range === 'all') {
            start = new Date(2000, 0, 1);
        } else {
            const days = Number(range);
            start = new Date(now.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
        }
        // Normalise start to local midnight and end to end-of-day (matches dateHelpers.js
        // so numbers line up with the Overview page).
        if (start && range !== 'custom') {
            start.setHours(0, 0, 0, 0);
        }
        if (end && range !== 'custom') {
            end = new Date(end.getTime());
            end.setHours(23, 59, 59, 999);
        }
        return { start, end };
    };

    // Memoized date range calculation with reduced re-render frequency
    const { start, end } = useMemo(() => {
        // Only calculate when necessary - avoid excessive re-renders
        if (range === 'custom' && (!customStart || !customEnd) && !customRangeApplied) {
            // Return placeholder dates for custom range without values
            const now = new Date();
            return { start: now, end: now };
        }
        
        const range_obj = getDateRange(range, customStart, customEnd);
        
        // Validate the calculated dates before logging
        const startValid = range_obj.start && !isNaN(range_obj.start.getTime());
        const endValid = range_obj.end && !isNaN(range_obj.end.getTime());
        
        // Only log when range changes significantly to reduce console spam
        if (startValid && endValid) {
            console.log('[SalesRepReports] Date range calculated:', {
                range,
                start: range_obj.start.toISOString(),
                end: range_obj.end.toISOString(),
                customRangeApplied
            });
        }
        
        // Return fallback dates if calculation failed
        if (!startValid || !endValid) {
            const now = new Date();
            console.warn('[SalesRepReports] Invalid date calculation, returning current date');
            return { start: now, end: now };
        }
        
        return range_obj;
    }, [range, customStart, customEnd, customRangeApplied]); // Added customRangeApplied to dependencies

    // OPTIMIZED: Filter orders with server-side date filtering for custom ranges
    const filteredOrders = useMemo(() => {
        // Use cached data for standard ranges, but for custom ranges always use live data
        if (usingCache && cachedData && range !== 'custom') return [];
        
        // Safety check for valid dates to prevent infinite loops
        if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
            console.warn('[SalesRepReports] Invalid date range, returning empty orders:', { start, end });
            return [];
        }
        
        // Early return if no orders to process
        if (!orders || orders.length === 0) {
            console.log('[SalesRepReports] No orders available for filtering');
            return [];
        }
        
        // For custom ranges, orders are already date-filtered by Firestore query
        // For other ranges, apply client-side filtering
        let ords = orders;
        if (range !== 'custom' || !customRangeApplied) {
            // Apply date filtering for non-custom ranges
            // Use YYYY-MM-DD string comparison for date_internal to avoid timezone issues.
            // new Date('YYYY-MM-DD') parses as UTC midnight, while new Date(y,m,d) is local midnight.
            const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
            const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
            
            let filteredCount = 0;
            ords = orders.filter(order => {
                // Early returns for performance
                if (range === 'all') return true;
                
                // Prefer string comparison on date_internal (timezone-safe)
                if (order.date_internal) {
                    const inRange = order.date_internal >= startStr && order.date_internal <= endStr;
                    if (inRange) filteredCount++;
                    return inRange;
                }
                
                // Fallback to dateObj for orders without date_internal
                if (!order.dateObj || isNaN(order.dateObj.getTime())) return false;
                
                try {
                    const inRange = order.dateObj >= start && order.dateObj <= end;
                    if (inRange) filteredCount++;
                    return inRange;
                } catch (error) {
                    console.warn('[SalesRepReports] Date comparison error:', error, { order: order.id });
                    return false;
                }
            });
            
            console.log('[SalesRepReports] Client-side date filtering applied:', {
                rangeType: range,
                filteredCount: filteredCount
            });
        } else {
            console.log('[SalesRepReports] Using server-side filtered orders:', {
                orderCount: orders.length,
                rangeType: 'custom'
            });
        }

        // Apply rep filter if needed
        let finalOrders = ords;
        if (selectedReps.length > 0) {
            finalOrders = ords.filter(order =>
                order.commissions_displayNames?.some(name =>
                    selectedReps.some(rep => rep.toLowerCase() === name?.toLowerCase())
                )
            );
        }

        // Exclude quotes (matches OrderReportsPage behavior)
        const isQuote = (o) => {
            const v = o.is_quote;
            return v === 1 || v === '1' || v === true || parseInt(v) === 1;
        };
        const nonQuoteOrders = finalOrders.filter(o => !isQuote(o));

        console.log('[SalesRepReports] Orders filtering completed:', {
            totalOrders: orders.length,
            afterDateFilter: ords.length,
            beforeQuoteFilter: finalOrders.length,
            finalOrdersCount: nonQuoteOrders.length,
            quotesRemoved: finalOrders.length - nonQuoteOrders.length,
            hasRepFilter: selectedReps.length > 0,
            usingServerSideFilter: range === 'custom' && customRangeApplied
        });

        return nonQuoteOrders;
    }, [orders, start, end, selectedReps, range, usingCache, cachedData, customRangeApplied]);

    // REMOVED: Excessive debug logging that was causing performance issues

    // Filter opportunities
    const filteredOpps = useMemo(() => {
        // Use cached data for standard ranges, but for custom ranges always use live data
        if (usingCache && cachedData && range !== 'custom') return [];
        
        // Safety check for valid dates to prevent infinite loops
        if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
            console.warn('[SalesRepReports] Invalid date range, returning empty opportunities:', { start, end });
            return [];
        }
        
        let opps = opportunities.filter(opp => {
            const oppDate = opp.startDate?.toDate?.() 
                ? opp.startDate.toDate() 
                : opp.startDate 
                    ? new Date(opp.startDate)
                    : opp.dueDate?.toDate?.()
                        ? opp.dueDate.toDate()
                        : opp.dueDate
                            ? new Date(opp.dueDate)
                            : opp.date?.toDate?.()
                                ? opp.date.toDate()
                                : opp.date
                                    ? new Date(opp.date)
                                    : null;
            
            if (range === 'all') return true;
            return oppDate && oppDate >= start && oppDate <= end;
        });

        if (selectedReps.length > 0) {
            opps = opps.filter(opp => {
                const oppUser = users.find(u => u.uid === opp.uid);
                return oppUser && selectedReps.includes(oppUser.displayName);
            });
        }

        return opps;
    }, [opportunities, start, end, selectedReps, users, range, usingCache, cachedData]);

    // Build set of rep names hidden from reports (users with hideFromReports flag)
    const hiddenRepNames = useMemo(() => {
        const names = new Set();
        users.forEach(u => {
            if (u.hideFromReports && u.displayName) {
                names.add(u.displayName);
            }
        });
        return names;
    }, [users]);

    // Rep performance calculation
    const repPerformance = useMemo(() => {
        // Return empty array if custom range is selected but not applied
        if (range === 'custom' && !customRangeApplied) {
            return [];
        }
        
        // Always use cached data when available and no rep filter
        if (usingCache && cachedData && cachedData.repPerformance) {
            
            // DEBUG: Log cached data summary for comparison
            const totalCachedRevenue = cachedData.repPerformance.reduce((sum, rep) => sum + (rep.revenue || 0), 0);
            const totalCachedOrders = cachedData.repPerformance.reduce((sum, rep) => sum + (rep.orders || 0), 0);
            
            // Reduced logging - only when needed for debugging
            if (process.env.NODE_ENV === 'development' && false) { // Disabled by default
                console.log('[SalesRepReports] CACHED DATA SUMMARY:');
                console.log('  - Cache Range:', range);
                console.log('  - Total Revenue: $' + totalCachedRevenue.toFixed(2));
                console.log('  - Total Orders:', totalCachedOrders);
                console.log('  - Rep Count:', cachedData.repPerformance.length);
            }
            
            // If no rep filter, return all cached data (excluding hidden reps)
            if (selectedReps.length === 0) {
                const mapped = cachedData.repPerformance
                    .filter(rep => !hiddenRepNames.has(rep.displayName || rep.name))
                    .map(rep => ({
                        name: rep.displayName || rep.name,
                        shippedRevenue: rep.shippedRevenue || rep.revenue || 0, // Fallback to revenue if shippedRevenue not available
                        totalOrdered: rep.totalOrdered || rep.revenue || 0, // Fallback to revenue if totalOrdered not available
                        orders: rep.orders || 0,
                        opportunities: rep.opportunities || 0,
                        wonOpps: rep.wonOpps || 0,
                        lostOpps: rep.lostOpps || 0,
                        activeOpps: rep.activeOpps || 0,
                        pipelineValue: rep.pipelineValue || 0
                    }));
                return mapped;
            }
            
            // If rep filter applied, filter cached data (excluding hidden reps)
            const filtered = cachedData.repPerformance
                .filter(rep => !hiddenRepNames.has(rep.displayName || rep.name) && selectedReps.includes(rep.displayName || rep.name))
                .map(rep => ({
                    name: rep.displayName || rep.name,
                    shippedRevenue: rep.shippedRevenue || rep.revenue || 0, // Fallback to revenue if shippedRevenue not available
                    totalOrdered: rep.totalOrdered || rep.revenue || 0, // Fallback to revenue if totalOrdered not available
                    orders: rep.orders || 0,
                    opportunities: rep.opportunities || 0,
                    wonOpps: rep.wonOpps || 0,
                    lostOpps: rep.lostOpps || 0,
                    activeOpps: rep.activeOpps || 0,
                    pipelineValue: rep.pipelineValue || 0
                }));
            return filtered;
        }

        const perfMap = {};
        
        // ONLY get sales reps from orders (not from users collection)
        // Revenue allocation strategy: Assign full amount to primary rep (first in commissions array)
        filteredOrders.forEach(order => {
            if (Array.isArray(order.commissions_displayNames) && order.commissions_displayNames.length > 0) {
                // Assign full revenue to primary rep (index 0) to avoid revenue splitting
                // This ensures the primary sales rep gets full credit for the order
                const primaryRepName = order.commissions_displayNames[0];
                
                if (hiddenRepNames.has(primaryRepName)) return;
                if (!perfMap[primaryRepName]) {
                    perfMap[primaryRepName] = {
                        name: primaryRepName,
                        shippedRevenue: 0,
                        totalOrdered: 0,
                        orders: 0,
                        opportunities: 0,
                        wonOpps: 0,
                        lostOpps: 0,
                        activeOpps: 0,
                        pipelineValue: 0
                    };
                }
                
                // Calculate shipped vs total ordered amounts
                const orderAmount = order.amount || 0;
                const amountShipped = order.amount_shipped || 0;
                
                // Debug: Log first few orders to verify field values
                if (perfMap[primaryRepName].orders === 0 && process.env.NODE_ENV === 'development') {
                    console.log(`[SalesRepReports] Sample order for ${primaryRepName}:`, {
                        orderId: order.id,
                        amount: orderAmount,
                        amount_shipped: amountShipped,
                        hasAmountShipped: order.amount_shipped !== undefined,
                        allAmountFields: {
                            amount: order.amount,
                            amount_shipped: order.amount_shipped,
                            amount_open: order.amount_open,
                            amount_subtotal: order.amount_subtotal
                        }
                    });
                }
                
                // Shipped revenue: only count what has actually been shipped
                perfMap[primaryRepName].shippedRevenue += amountShipped;
                
                // Total ordered: all order amounts regardless of status
                perfMap[primaryRepName].totalOrdered += orderAmount;
                
                perfMap[primaryRepName].orders += 1;
                
                // Note: Secondary reps (index 1+) do not receive revenue/order credit
                // This prevents revenue double-counting while giving primary rep full attribution
            }
        });
        
        // DEBUG: Compare data calculation with cache when debugging needed
        if (!loading && !usingCache && filteredOrders.length > 0 && process.env.NODE_ENV === 'development' && false) { // Disabled by default
            const totalLiveRevenue = filteredOrders.reduce((sum, order) => sum + (order.amount || 0), 0);
            const liveOrderCount = filteredOrders.length;
            
            console.log('[SalesRepReports] LIVE DATA SUMMARY:');
            console.log('  - Range Type:', range === 'custom' ? 'Custom (' + customStart + ' to ' + customEnd + ')' : range + ' days');
            console.log('  - Total Revenue: $' + totalLiveRevenue.toFixed(2));
            console.log('  - Total Orders:', liveOrderCount);
        }
        
        // Add opportunity counts ONLY for reps that exist in orders
        filteredOpps.forEach(opp => {
            // Match opportunity by finding the user via uid and checking their displayName
            if (opp.uid) {
                const oppUser = users.find(u => u.uid === opp.uid);
                if (oppUser && oppUser.displayName && perfMap[oppUser.displayName]) {
                    const perf = perfMap[oppUser.displayName];
                    perf.opportunities += 1;
                    
                    if (opp.stage === 'Closed Won') {
                        perf.wonOpps += 1;
                    } else if (opp.stage === 'Closed Lost') {
                        perf.lostOpps += 1;
                    } else {
                        perf.activeOpps += 1;
                        perf.pipelineValue += Number(opp.value) || 0;
                    }
                }
            }
        });
        
        const result = Object.values(perfMap).sort((a, b) => b.totalOrdered - a.totalOrdered);
        
        // Reduced logging frequency - only log when there are errors or in debug mode
        if (!loading && (result.length === 0 && orders.length > 0) && process.env.NODE_ENV === 'development') {
            // Only log when there might be an issue (no reps found despite having orders)
            const repTotalOrdered = result.reduce((sum, rep) => sum + rep.totalOrdered, 0);
            const repShippedRevenue = result.reduce((sum, rep) => sum + rep.shippedRevenue, 0);
            const repTotalOrders = result.reduce((sum, rep) => sum + rep.orders, 0);
            
            console.log('[SalesRepReports] Rep performance calculated:', {
                usingCache,
                dataSource: usingCache ? 'cached' : 'live',
                totalReps: result.length,
                totalOrdered: repTotalOrdered,
                shippedRevenue: repShippedRevenue,
                totalOrders: repTotalOrders,
                ordersProcessed: filteredOrders.length
            });
            
            // Check for revenue calculation discrepancy only in development
            if (!usingCache && filteredOrders.length > 0) {
                const directOrderSum = filteredOrders.reduce((sum, order) => sum + (order.amount || 0), 0);
                const difference = Math.abs(repTotalOrdered - directOrderSum);
                
                if (difference > 1) { // More than $1 difference
                    console.warn('[SalesRepReports] REVENUE CALCULATION DISCREPANCY:');
                    console.warn('  - Direct order sum: $' + directOrderSum.toFixed(2));
                    console.warn('  - Rep performance sum: $' + repTotalOrdered.toFixed(2));
                    console.warn('  - Difference: $' + difference.toFixed(2));
                }
            }
        }
        
        return result;
    }, [usingCache, cachedData, selectedReps, filteredOrders, filteredOpps, users, range, customRangeApplied, hiddenRepNames]);

    // All reps for dropdown
    const allReps = useMemo(() => {
        // Return empty array if custom range is selected but not applied
        if (range === 'custom' && !customRangeApplied) {
            return [];
        }
        
        if (usingCache && cachedData && cachedData.repPerformance) {
            // Map cached data to match expected structure, excluding disabled users
            return cachedData.repPerformance
                .filter(rep => !hiddenRepNames.has(rep.displayName || rep.name))
                .map(rep => ({
                    name: rep.displayName || rep.name,
                    revenue: rep.revenue || 0,
                    orders: rep.orders || 0,
                    opportunities: rep.opportunities || 0
                }));
        }
        
        const repSet = new Set();
        
        // ONLY get sales reps who are PRIMARY reps (matching performance calculation)
        // This ensures chart data matches the performance table
        orders.forEach(order => {
            if (Array.isArray(order.commissions_displayNames) && order.commissions_displayNames.length > 0) {
                // Only include primary rep (index 0) to match revenue allocation strategy
                const primaryRepName = order.commissions_displayNames[0];
                if (!hiddenRepNames.has(primaryRepName)) {
                    repSet.add(primaryRepName);
                }
            }
        });
        
        return Array.from(repSet).sort().map(name => ({ name }));
    }, [orders, usingCache, cachedData, range, customRangeApplied, hiddenRepNames]);

    // Initialize pending reps when dropdown opens
    useEffect(() => {
        if (showRepDropdown) {
            setPendingReps(selectedReps);
        }
    }, [showRepDropdown, selectedReps]);

    const handleApplyRepFilter = () => {
        setShowRepDropdown(false);
    };

    const handleCancelRepFilter = () => {
        setPendingReps(selectedReps);
        setShowRepDropdown(false);
    };

    // Detect when any pending filter differs from applied
    const filtersChanged = useMemo(() => {
        const repChanged = JSON.stringify(pendingReps) !== JSON.stringify(selectedReps);
        const dateChanged = pendingRange !== range || pendingCustomStart !== customStart || pendingCustomEnd !== customEnd;
        return repChanged || dateChanged;
    }, [pendingReps, selectedReps, pendingRange, range, pendingCustomStart, customStart, pendingCustomEnd, customEnd]);

    // Apply all filters — copies pending to URL state (batched via microtask)
    const handleApplyFilters = useCallback(() => {
        setRange(pendingRange);
        setCustomStart(pendingCustomStart);
        setCustomEnd(pendingCustomEnd);
        setSelectedReps(pendingReps);
        if (pendingRange === 'custom' && pendingCustomStart && pendingCustomEnd) {
            setCustomRangeApplied(true);
        }
    }, [pendingRange, pendingCustomStart, pendingCustomEnd, pendingReps, setRange, setCustomStart, setCustomEnd, setSelectedReps, setCustomRangeApplied]);

    // Clear all filters to defaults
    const handleClearFilters = useCallback(() => {
        setPendingRange('30');
        setPendingCustomStart('');
        setPendingCustomEnd('');
        setPendingReps([]);
        setRange('30');
        setCustomStart('');
        setCustomEnd('');
        setSelectedReps([]);
        setCustomRangeApplied(false);
    }, [setRange, setCustomStart, setCustomEnd, setSelectedReps, setCustomRangeApplied]);

    const hasActiveFilters = useMemo(() => {
        return pendingRange !== '30' || pendingCustomStart !== '' || pendingCustomEnd !== '' || pendingReps.length > 0;
    }, [pendingRange, pendingCustomStart, pendingCustomEnd, pendingReps]);

    // Sync pending state from URL on external changes (browser back/forward)
    useEffect(() => { setPendingRange(range || '30'); }, [range]);
    useEffect(() => { setPendingCustomStart(customStart || ''); }, [customStart]);
    useEffect(() => { setPendingCustomEnd(customEnd || ''); }, [customEnd]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (repDropdownRef.current && !repDropdownRef.current.contains(event.target)) {
                setShowRepDropdown(false);
            }
        };

        if (showRepDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showRepDropdown]);



    // Prepare chart configurations
    useEffect(() => {
        if (loading) return;

        // Clear charts when no data (custom range not applied) or when custom range selected but not applied
        if (repPerformance.length === 0 || (range === 'custom' && !customRangeApplied)) {
            // Set empty charts instead of null
            setRepChartConfig({
                type: 'bar',
                data: {
                    labels: [],
                    datasets: [{
                        label: 'Revenue',
                        data: [],
                        backgroundColor: '#10B981',
                        borderColor: '#059669',
                        borderWidth: 1
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            top: 20,
                            right: 20,
                            bottom: 20,
                            left: 20
                        }
                    },
                    plugins: {
                        title: {
                            display: true,
                            text: 'Top Sales Reps by Revenue'
                        },
                        legend: {
                            display: false
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                callback: function(value) {
                                    return '$' + value.toLocaleString();
                                }
                            }
                        }
                    }
                }
            });

            setSalesChartConfig({
                type: 'line',
                data: {
                    labels: [],
                    datasets: []
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            top: 20,
                            right: 20,
                            bottom: 20,
                            left: 20
                        }
                    },
                    plugins: {
                        title: {
                            display: true,
                            text: 'Sales Performance by Rep Over Time'
                        },
                        legend: {
                            display: true,
                            position: 'bottom'
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                callback: function(value) {
                                    return '$' + value.toLocaleString();
                                }
                            }
                        }
                    }
                }
            });
            return;
        }

        // Top Sales Reps by Revenue Chart Configuration
        if (repPerformance.length > 0) {
            const topReps = repPerformance.slice(0, 10); // Top 10 reps
            const repLabels = topReps.map(r => r.name);
            const repShippedRevenue = topReps.map(r => r.shippedRevenue);
            const repTotalOrdered = topReps.map(r => r.totalOrdered);

            setRepChartConfig({
                type: 'bar',
                data: {
                    labels: repLabels,
                    datasets: [
                        {
                            label: 'Shipped Revenue',
                            data: repShippedRevenue,
                            backgroundColor: '#10B981',
                            borderColor: '#059669',
                            borderWidth: 1
                        },
                        {
                            label: 'Amount Ordered',
                            data: repTotalOrdered,
                            backgroundColor: '#3B82F6',
                            borderColor: '#2563EB',
                            borderWidth: 1
                        }
                    ]
                },
                options: {
                    plugins: {
                        title: {
                            display: true,
                            text: 'Top Sales Reps by Revenue'
                        },
                        legend: {
                            display: true,
                            position: 'top'
                        },
                        tooltip: {
                            callbacks: {
                                label: function(context) {
                                    const label = context.dataset.label || '';
                                    const value = context.parsed.y;
                                    return label + ': $' + value.toLocaleString('en-US', { 
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2 
                                    });
                                }
                            }
                        },oltip: {
                            callbacks: {
                                label: function(context) {
                                    const value = context.parsed.y;
                                    return 'Revenue: $' + value.toLocaleString('en-US', { 
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2 
                                    });
                                }
                            }
                        },
                        zoom: {
                            pan: {
                                enabled: true,
                                mode: 'xy'
                            },
                            zoom: {
                                wheel: {
                                    enabled: true,
                                },
                                pinch: {
                                    enabled: true
                                },
                                mode: 'xy',
                            }
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                callback: function(value) {
                                    return '$' + value.toLocaleString();
                                }
                            }
                        }
                    }
                }
            });
        }

        // Sales Performance Over Time Chart Configuration
        if (repPerformance.length > 0) {
            // Use cached time series data if available
            if (usingCache && cachedData && cachedData.timeSeriesByRep) {
                // Determine if we should use daily or monthly data
                // Daily: 7, 14, 30 days
                // Monthly: 90 days, ytd
                const useDaily = ['7', '14', '30'].includes(range);
                const timeSeriesData = useDaily 
                    ? (cachedData.timeSeriesByRep.daily || {})
                    : (cachedData.timeSeriesByRep.monthly || {});
                                
                if (Object.keys(timeSeriesData).length > 0) {
                    // Get all unique dates from all reps
                    const allDates = new Set();
                    Object.values(timeSeriesData).forEach(repData => {
                        Object.keys(repData).forEach(date => allDates.add(date));
                    });
                    
                    // FILTER dates to the actual selected range!
                    const sortedDates = Array.from(allDates)
                        .filter(dateStr => {
                            // Parse the YYYY-MM-DD string as local date to avoid timezone issues
                            const [year, month, day] = dateStr.split('-');
                            const date = new Date(year, month - 1, day); // month is 0-indexed
                            return date >= start && date <= end;
                        })
                        .sort();
                    
                    // Format labels based on daily vs monthly
                    const labels = sortedDates.map(d => {
                        if (useDaily) {
                            // Daily: MM/DD format
                            const parts = d.split('-');
                            return `${parts[1]}/${parts[2]}`;
                        } else {
                            // Monthly: "Jan 2025" format
                            const [year, month] = d.split('-');
                            const date = new Date(year, month - 1);
                            return `${date.toLocaleString('default', { month: 'short' })} ${year}`;
                        }
                    });
                    
                    // Create dataset for each rep
                    const colors = [
                        '#EF4444', '#3B82F6', '#10B981', '#FBBF24', '#8B5CF6',
                        '#F97316', '#14B8A6', '#EC4899', '#A78BFA', '#6B7280'
                    ];
                    
                    const datasets = Object.entries(timeSeriesData)
                        .filter(([repName]) => !hiddenRepNames.has(repName))
                        .slice(0, 10).map(([repName, repData], idx) => {
                        const data = sortedDates.map(date => {
                            const entry = repData[date];
                            return entry ? (typeof entry === 'object' ? entry.revenue || 0 : entry) : 0;
                        });
                        
                        return {
                            label: repName,
                            data: data,
                            borderColor: colors[idx % colors.length],
                            backgroundColor: colors[idx % colors.length] + '40', // 40% opacity
                            borderWidth: 2,
                            tension: 0.1,
                            pointRadius: 3,
                            pointHoverRadius: 6
                        };
                    });
                    
                    setSalesChartConfig({
                        type: 'line',
                        data: {
                            labels: labels,
                            datasets: datasets
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            layout: {
                                padding: {
                                    top: 20,
                                    right: 30,
                                    bottom: 20,
                                    left: 20
                                }
                            },
                            plugins: {
                                title: {
                                    display: true,
                                    text: `Sales Performance by Rep Over Time (${useDaily ? 'Daily' : 'Monthly'})`
                                },
                                legend: {
                                    position: 'bottom',
                                    labels: {
                                        boxWidth: 12,
                                        padding: 10
                                    }
                                },
                                tooltip: {
                                    mode: 'point',
                                    intersect: true,
                                    callbacks: {
                                        label: function(context) {
                                            const label = context.dataset.label || '';
                                            const value = context.parsed.y;
                                            return label + ': $' + value.toLocaleString('en-US', { 
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2 
                                            });
                                        }
                                    }
                                },
                                zoom: {
                                    pan: {
                                        enabled: true,
                                        mode: 'xy'
                                    },
                                    zoom: {
                                        wheel: {
                                            enabled: true,
                                        },
                                        pinch: {
                                            enabled: true
                                        },
                                        mode: 'xy',
                                    }
                                }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: function(value) {
                                            return '$' + value.toLocaleString();
                                        }
                                    }
                                }
                            },
                            interaction: {
                                mode: 'point',
                                intersect: true
                            },
                            hover: {
                                mode: 'nearest',
                                intersect: false,
                                animationDuration: 0
                            }
                        }
                    });
                }
            } else {
                // Generate time series from live data when not using cache
                console.log('[SalesRepReports] Generating time series from live data');
                
                if (filteredOrders.length > 0) {
                    // Generate time series from filtered orders
                    const repTimeSeriesData = {};
                    
                    // First, collect primary rep names from filtered orders (matching performance table)
                    const repNames = new Set();
                    filteredOrders.forEach(order => {
                        if (Array.isArray(order.commissions_displayNames) && order.commissions_displayNames.length > 0) {
                            // Only include primary rep (index 0) to match revenue allocation strategy
                            const primaryRepName = order.commissions_displayNames[0];
                            if (!hiddenRepNames.has(primaryRepName)) {
                                repNames.add(primaryRepName);
                            }
                        }
                    });
                    
                    // Initialize time series for each rep
                    repNames.forEach(repName => {
                        repTimeSeriesData[repName] = {};
                    });
                    
                    // Populate data by processing each order - assign to primary rep only
                    filteredOrders.forEach(order => {
                        if (order.dateObj && Array.isArray(order.commissions_displayNames) && order.commissions_displayNames.length > 0) {
                            // Use local date formatting to avoid UTC timezone issues
                            const year = order.dateObj.getFullYear();
                            const month = String(order.dateObj.getMonth() + 1).padStart(2, '0');
                            const day = String(order.dateObj.getDate()).padStart(2, '0');
                            const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD format in local time
                            
                            // Revenue allocation strategy: Assign full credit to primary rep only
                            const primaryRepName = order.commissions_displayNames[0];
                            if (repTimeSeriesData[primaryRepName] && !repTimeSeriesData[primaryRepName][dateStr]) {
                                repTimeSeriesData[primaryRepName][dateStr] = { revenue: 0, orders: 0 };
                            }
                            if (repTimeSeriesData[primaryRepName]) {
                                repTimeSeriesData[primaryRepName][dateStr].revenue += order.amount || 0;
                                repTimeSeriesData[primaryRepName][dateStr].orders += 1;
                            }
                        }
                    });
                    
                    // Get all unique dates and sort them
                    const allDates = new Set();
                    Object.values(repTimeSeriesData).forEach(repData => {
                        Object.keys(repData).forEach(date => allDates.add(date));
                    });
                    const sortedDates = Array.from(allDates).sort();
                    
                    if (sortedDates.length > 0) {
                        // Format labels for display
                        const labels = sortedDates.map(d => {
                            // Parse the YYYY-MM-DD string as local date to avoid timezone issues
                            const [year, month, day] = d.split('-');
                            const date = new Date(year, month - 1, day); // month is 0-indexed
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        });
                        
                        // Create datasets for each rep (limit to top 10)
                        const colors = [
                            '#EF4444', '#3B82F6', '#10B981', '#FBBF24', '#8B5CF6',
                            '#F97316', '#14B8A6', '#EC4899', '#A78BFA', '#6B7280'
                        ];
                        
                        const datasets = Object.entries(repTimeSeriesData).slice(0, 10).map(([repName, repData], idx) => {
                            const data = sortedDates.map(date => repData[date]?.revenue || 0);
                            
                            return {
                                label: repName,
                                data: data,
                                borderColor: colors[idx % colors.length],
                                backgroundColor: colors[idx % colors.length] + '20', // Add transparency
                                borderWidth: 2,
                                fill: false,
                                tension: 0.1
                            };
                        });
                        
                        setSalesChartConfig({
                            type: 'line',
                            data: {
                                labels: labels,
                                datasets: datasets
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                layout: {
                                    padding: {
                                        top: 20,
                                        right: 20,
                                        bottom: 20,
                                        left: 20
                                    }
                                },
                                plugins: {
                                    title: {
                                        display: true,
                                        text: 'Sales Performance by Rep Over Time'
                                    },
                                    legend: {
                                        display: true,
                                        position: 'bottom'
                                    },
                                    tooltip: {
                                        mode: 'point',
                                        intersect: true,
                                        callbacks: {
                                            label: function(context) {
                                                const label = context.dataset.label || '';
                                                const value = context.parsed.y;
                                                return label + ': $' + value.toLocaleString('en-US', { 
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2 
                                                });
                                            }
                                        }
                                    },
                                    zoom: {
                                        pan: {
                                            enabled: true,
                                            mode: 'xy'
                                        },
                                        zoom: {
                                            wheel: {
                                                enabled: true,
                                            },
                                            pinch: {
                                                enabled: true
                                            },
                                            mode: 'xy',
                                        }
                                    }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            callback: function(value) {
                                                return '$' + value.toLocaleString();
                                            }
                                        }
                                    }
                                },
                                interaction: {
                                    mode: 'point',
                                    intersect: true
                                },
                                hover: {
                                    mode: 'nearest',
                                    intersect: false,
                                    animationDuration: 0
                                }
                            }
                        });
                    }
                } else {
                    // No data available - this should be handled by the empty chart logic above
                    console.log('[SalesRepReports] No filtered orders available for live time series');
                }
            }
        }
    }, [loading, repPerformance, cachedData, usingCache, range, customRangeApplied, filteredOrders, hiddenRepNames]);

    // Render charts (implementation would go here - similar to ReportsPage)
    // For brevity, I'll add a simplified version

    const handleExport = (format) => {
        const headers = ['Sales Rep', 'Shipped Revenue', 'Amount Ordered', 'Orders', 'Open Opps', 'Won Opps', 'Lost Opps', 'Pipeline Value'];
        const data = repPerformance;
        const rowMapper = (rep) => [
            rep.name || '',
            `$${(rep.shippedRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            `$${(rep.totalOrdered || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            rep.orders || 0,
            rep.activeOpps || 0,
            rep.wonOpps || 0,
            rep.lostOpps || 0,
            `$${(rep.pipelineValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ];
        const dateLabel = range === 'ytd' ? 'YTD' : range === 'custom' ? 'custom' : `${range}d`;
        exportData({
            filename: `sales_rep_reports_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
            format,
            headers,
            data,
            rowMapper,
            options: format.toLowerCase() === 'pdf' ? {
                title: `Sales Rep Reports - Performance (${dateLabel})`,
                fontSize: 7,
                orientation: 'landscape',
                pageSize: 'a4'
            } : {
                title: `Sales Rep Reports - Performance (${dateLabel})`,
                sheetName: 'Sales Rep Reports',
                columnWidths: [20, 18, 18, 10, 10, 10, 10, 18]
            }
        });
    };

    if (loading) {
        return (
            <main className="flex-grow p-6">
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading reports...</p>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            
            {/* Header */}
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                        <svg className="w-8 h-8 mr-3 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Sales Rep Reports
                    </h1>
                    <p className="text-gray-600 mt-2">Performance metrics and rankings for sales representatives</p>
                </div>
                {can(FEATURE_PERMISSIONS.REPORTS_EXPORT) && (
                    <ExportButton
                        onExport={handleExport}
                        formats={['csv', 'xlsx', 'pdf']}
                        disabled={loading || repPerformance.length === 0}
                        tooltipText="Export sales rep performance data"
                        label="Export"
                        className="bg-indigo-600 hover:bg-indigo-700"
                    />
                )}
            </div>

            {/* Date Range & Filters */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    {/* Sales Rep Filter */}
                    {allReps.length > 1 && (
                        <div className="w-full md:w-64">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Filter by Sales Rep
                            </label>
                            <div className="relative" ref={repDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowRepDropdown(!showRepDropdown)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-left flex items-center justify-between"
                                >
                                    <span className="block truncate">
                                        {pendingReps.length === 0 
                                            ? 'All Sales Reps' 
                                            : pendingReps.length === allReps.length
                                            ? 'All Sales Reps'
                                            : `${pendingReps.length} selected`}
                                    </span>
                                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>

                                {showRepDropdown && (
                                    <div className="absolute z-50 mt-2 w-80 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-y-auto">
                                        <div className="p-3 border-b border-gray-200 bg-gray-50">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-semibold text-gray-700">Select Sales Reps</span>
                                                <button
                                                    onClick={() => {
                                                        if (pendingReps.length === allReps.length) {
                                                            setPendingReps([]);
                                                        } else {
                                                            setPendingReps(allReps.map(r => r.name));
                                                        }
                                                    }}
                                                    className="text-xs text-indigo-600 hover:text-indigo-800"
                                                >
                                                    {pendingReps.length === allReps.length ? 'Clear All' : 'Select All'}
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                {pendingReps.length} of {allReps.length} selected
                                            </p>
                                        </div>
                                        
                                        <div className="p-2">
                                            {allReps.map(rep => (
                                                <label
                                                    key={rep.name}
                                                    className="flex items-center px-2 py-2 hover:bg-gray-50 rounded cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={pendingReps.includes(rep.name)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setPendingReps([...pendingReps, rep.name]);
                                                            } else {
                                                                setPendingReps(pendingReps.filter(r => r !== rep.name));
                                                            }
                                                        }}
                                                        className="mr-2"
                                                    />
                                                    <span className="text-sm text-gray-700">{rep.name}</span>
                                                </label>
                                            ))}
                                        </div>

                                        <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
                                            <button
                                                onClick={handleCancelRepFilter}
                                                className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleApplyRepFilter}
                                                className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded hover:bg-indigo-700"
                                            >
                                                OK
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Date
                        </label>
                        <DateRangeSelector
                            range={pendingRange}
                            setRange={setPendingRange}
                            customStart={pendingCustomStart}
                            setCustomStart={setPendingCustomStart}
                            customEnd={pendingCustomEnd}
                            setCustomEnd={setPendingCustomEnd}
                            hideClear
                            defaultRange="30"
                        />
                    </div>

                    {/* Apply and Clear Buttons */}
                    <div className="flex items-end gap-2">
                        <button
                            onClick={handleApplyFilters}
                            disabled={!filtersChanged}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                filtersChanged
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {filtersChanged ? '✓ Apply Filters' : 'Applied'}
                        </button>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Cache indicator */}
            {usingCache && cachedData && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-green-800">
                            Using cached data (last updated {Math.round((Date.now() - cachedData.lastUpdated.toDate().getTime()) / 60000)}m ago)
                        </span>
                    </div>
                    {hasPermission(user, FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH) && (
                        <Tooltip text="Refresh the reports cache with latest data. Cache auto-refreshes every 8 hours. May take 30-60 seconds." maxWidth={300} position="left">
                            <button
                                onClick={handleManualRefresh}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                            >
                                Refresh Cache
                            </button>
                        </Tooltip>
                    )}
                </div>
            )}

            {/* Charts Grid - Compact Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Sales Performance Over Time Chart */}
                {salesChartConfig && (
                    <CompactChart
                        chartConfig={salesChartConfig}
                        title="Sales Performance by Rep Over Time"
                        height="h-72"
                    />
                )}

                {/* Top Sales Reps by Revenue Chart */}
                {repChartConfig && (
                    <CompactChart
                        chartConfig={repChartConfig}
                        title="Top Sales Reps by Revenue"
                        height="h-72"
                    />
                )}
            </div>

            {/* Sales Rep Performance Table - Full Width */}
            <div className="bg-white rounded-lg shadow overflow-x-auto mb-8">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900">Sales Rep Performance</h3>
                </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Rep</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        <Tooltip text="Revenue from orders that have been shipped (actual revenue received)">
                                            <span className="border-b border-dotted border-gray-400 cursor-help">Shipped Revenue</span>
                                        </Tooltip>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        <Tooltip text="Total amount of all orders placed (includes pending and shipped orders)">
                                            <span className="border-b border-dotted border-gray-400 cursor-help">Amount Ordered</span>
                                        </Tooltip>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Opportunities</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Won Opportunities</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lost Opportunities</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {repPerformance.slice(0, 10).map((rep, idx) => (
                                    <tr key={rep.name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{rep.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                                            ${rep.shippedRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ${rep.totalOrdered.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Math.round(rep.orders)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">{rep.activeOpps || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{rep.wonOpps || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">{rep.lostOpps || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                {(() => {
                                    const displayed = repPerformance.slice(0, 10);
                                    const totals = displayed.reduce((acc, rep) => ({
                                        shippedRevenue: acc.shippedRevenue + (rep.shippedRevenue || 0),
                                        totalOrdered: acc.totalOrdered + (rep.totalOrdered || 0),
                                        orders: acc.orders + (rep.orders || 0),
                                        activeOpps: acc.activeOpps + (rep.activeOpps || 0),
                                        wonOpps: acc.wonOpps + (rep.wonOpps || 0),
                                        lostOpps: acc.lostOpps + (rep.lostOpps || 0),
                                    }), { shippedRevenue: 0, totalOrdered: 0, orders: 0, activeOpps: 0, wonOpps: 0, lostOpps: 0 });
                                    return (
                                        <tr>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-900">Total</td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm text-green-700 font-bold">
                                                ${totals.shippedRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                                                ${totals.totalOrdered.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-gray-900">{Math.round(totals.orders)}</td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm text-blue-700 font-bold">{totals.activeOpps}</td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm text-green-700 font-bold">{totals.wonOpps}</td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm text-red-700 font-bold">{totals.lostOpps}</td>
                                        </tr>
                                    );
                                })()}
                            </tfoot>
                        </table>
                    </div>
                </div>
        </main>
    );
};

export default SalesRepReportsPage;
